import { sendToApi } from 'api/client'
import { HandbookV2, HandbookItem, ViewType, Workspace } from 'types'
import { getHandbookPassword } from 'hooks/useHandbookPasswords'

interface HandbookResponse {
  handbook: HandbookV2
}

export interface HandbookItemParams
  extends Pick<
    Partial<HandbookItem>,
    | 'title'
    | 'url'
    | 'favicon'
    | 'type'
    | 'document'
    | 'handbook_id'
    | 'parent_id'
    | 'position'
    | 'type'
    | 'folder'
    | 'nested_handbook'
    | 'handbook_mode'
    | 'database_mode'
  > {
  key?: string | null
  is_mention?: boolean
  default_view?: ViewType
}

export interface CreateHandbookItemParams
  // Required
  extends Pick<HandbookItem, 'type' | 'position'>,
    // Optionals
    Pick<
      Partial<HandbookItem>,
      | 'title'
      | 'url'
      | 'favicon'
      | 'document'
      | 'handbook_id'
      | 'parent_id'
      | 'folder'
      | 'nested_handbook'
      | 'handbook_mode'
      | 'database_mode'
    > {
  key?: string | null
  is_mention?: boolean
  default_view?: ViewType
}

export interface HandbookItemResponse {
  handbook_item: HandbookItem
  handbook?: Pick<HandbookV2, 'cover_doc'>
}

export type UpdateHandbookItemResponse = Required<HandbookItemResponse>

export interface NonMigratedHandbook {
  id: string
  name: string
  emoji: string
  seo_string: string
  path: string
}

export interface MigrateHandbookResponse {
  non_migrated_handbooks: NonMigratedHandbook[]
}

export function getHandbook(key: string) {
  const handbookPassword = getHandbookPassword(key)
  return sendToApi<HandbookResponse>(`/handbooks/${key}`, {
    params: {
      password: handbookPassword,
    },
  })
}

export function getHandbookWorkspace(key: string) {
  return sendToApi<{ company: Pick<Workspace, 'id' | 'key'> }>(`/handbooks/${key}/workspace`, {
    headers: { 'Content-Type': 'application/json' },
  })
}

export function updateHandbook(handbook: HandbookV2, values: Partial<HandbookV2>) {
  return sendToApi<HandbookResponse>(`/handbooks/${handbook.key}`, {
    method: 'PATCH',
    body: values,
  })
}

export function publishHandbook(handbook: HandbookV2) {
  return sendToApi<HandbookResponse>(`/handbooks/${handbook.key}/publish`, { method: 'POST' })
}

export function checkHandbookPassword(key: string, password: string) {
  return sendToApi<HandbookResponse>(`/handbooks/${key}/check_password`, {
    method: 'POST',
    body: { entered_password: password },
  })
}

export function unpublishHandbook(handbook: HandbookV2) {
  return sendToApi<HandbookResponse>(`/handbooks/${handbook.key}/unpublish`, { method: 'POST' })
}

export function deleteHandbook(handbook: HandbookV2) {
  return sendToApi<HandbookResponse>(`/handbooks/${handbook.key}`, { method: 'DELETE' })
}

export function withCustomPath(customDomain: string, customPath: string) {
  return sendToApi(`/handbooks/with_custom_path`, {
    params: { custom_path: customPath, custom_domain: customDomain },
  })
}

export function updateHandbookItem(itemId: number, values: Partial<HandbookItem>) {
  return sendToApi<UpdateHandbookItemResponse>(`/handbooks/items/${itemId}`, {
    method: 'PATCH',
    body: values,
  })
}

export function copyHandbookItem(itemId: number) {
  return sendToApi<HandbookItemResponse>(`/handbook_items/${itemId}/copy`, {
    method: 'POST',
  })
}

export function createHandbookItem(values: CreateHandbookItemParams) {
  return sendToApi<HandbookItemResponse>(`/handbook_items`, {
    method: 'POST',
    body: values,
  })
}

export function bulkCreateHandbookItem(
  handbooks: Pick<HandbookV2, 'key'>[],
  items: HandbookItemParams[],
  extra: object = {},
) {
  return sendToApi<{ handbook_items: HandbookItem }>(`/handbook_items/bulk`, {
    method: 'POST',
    body: {
      handbooks: handbooks.map((h) => h.key),
      items,
      ...extra,
    },
  })
}

export function deleteHandbookItem(item: HandbookItem, adopt: boolean = false) {
  return sendToApi<undefined>(`/handbooks/items/${item.id}`, {
    method: 'DELETE',
    body: { adopt },
  })
}

export function migrateHandbooks() {
  return sendToApi<MigrateHandbookResponse>(`/handbooks/migrate`, {
    method: 'POST',
  })
}
